/**
 * Скачивание файла из ответа axios.
 *
 * Имя берётся из Content-Disposition, а не собирается на клиенте: сервер уже
 * знает, как документ называется, и умеет кириллицу через RFC 5987. Чтобы это
 * работало, нужны две вещи одновременно — запрос должен возвращать весь ответ
 * (не `.then(r => r.data)`), а API — отдавать `expose_headers`, иначе браузер
 * заголовок в JS просто не пустит.
 */

/**
 * filename* важнее filename: ASCII-часть для кириллического имени вырождается
 * в мусор, и только звёздочная форма несёт настоящее имя.
 */
export function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;

  const encoded = /filename\*=\s*([^']*)'([^']*)'([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[3].trim().replace(/^"|"$/g, ''));
    } catch {
      // битое процент-экранирование — падаем на обычную форму ниже
    }
  }

  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i.exec(header);
  if (plain) return (plain[1] ?? plain[2] ?? '').trim() || fallback;

  return fallback;
}

function headerOf(response, name) {
  const headers = response?.headers;
  if (!headers) return null;
  // axios 1.x отдаёт AxiosHeaders с .get(), но в тестах и старых версиях это
  // обычный объект.
  return (typeof headers.get === 'function' ? headers.get(name) : headers[name]) || null;
}

export function downloadFromResponse(response, fallbackName) {
  const name = filenameFromDisposition(headerOf(response, 'content-disposition'), fallbackName);
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return name;
}

const STATUS_MESSAGES = {
  401: 'Требуется вход в систему',
  403: 'Доступно только администратору',
  404: 'Документ ещё не сформирован',
  415: 'Этот формат недоступен для такого артефакта',
  422: 'Артефакт не содержит текста для экспорта',
  500: 'Не удалось собрать документ',
};

/**
 * Внятный текст ошибки.
 *
 * При responseType: 'blob' тело JSON-ошибки приходит Blob'ом и не парсится
 * само — без этого пользователь видит только «Request failed with status code
 * 404».
 */
export async function describeDownloadError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      const message = parsed?.error?.message || parsed?.detail;
      if (message) return message;
    } catch {
      // тело не JSON — используем сообщение по статусу
    }
  } else if (data?.error?.message) {
    return data.error.message;
  }

  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (!error?.response) return 'Сервер недоступен';
  return `Не удалось скачать файл (код ${status})`;
}
